import type { ShopperProducts } from '@/scapi';
import type { ContentFolder } from '@/lib/api/content-folders.server';
import { createCategoryUrl } from '@/lib/category-url';
import { getMenuExternalUrl } from '@/lib/menu-link';
import { isCategoryInEvidence } from '@/lib/catalog-menu';

type Category = ShopperProducts.schemas['Category'];

/** Folder-only menu fields mapped onto the Category shape used by the catalog menu. */
export type CatalogMenuCategory = Category & {
    c_showSelfInMenu?: boolean;
    c_selfMenuLabel?: string;
};

export type FolderSelfLink = {
    href: string;
    label: string;
    inEvidence: boolean;
};

export function isContentFolderVisibleInMenu(folder: ContentFolder): boolean {
    return folder.showInMenu === true;
}

/** Recursively keeps only folders flagged with `showInMenu`. */
export function filterContentFoldersForMenu(folders: ContentFolder[]): ContentFolder[] {
    return folders.filter(isContentFolderVisibleInMenu).map((folder) => ({
        ...folder,
        folders: folder.folders ? filterContentFoldersForMenu(folder.folders) : undefined,
    }));
}

export function findContentFolderById(folders: ContentFolder[], id: string): ContentFolder | undefined {
    for (const folder of folders) {
        if (folder.id === id) return folder;
        if (folder.folders?.length) {
            const result = findContentFolderById(folder.folders, id);
            if (result) return result;
        }
    }
    return undefined;
}

function mapFolderToCategory(folder: ContentFolder): CatalogMenuCategory {
    const externalUrl = getMenuExternalUrl(folder.externalUrl);
    const selfMenuLabel = folder.selfMenuLabel?.trim();

    return {
        id: folder.id,
        name: folder.name,
        c_url: folder.path,
        ...(externalUrl ? { c_externalUrl: externalUrl } : {}),
        ...(folder.customerGroupId?.length ? { c_customerGroupId: folder.customerGroupId } : {}),
        ...(folder.inEvidence ? { c_inEvidence: true } : {}),
        ...(folder.showSelfInMenu ? { c_showSelfInMenu: true } : {}),
        ...(selfMenuLabel ? { c_selfMenuLabel: selfMenuLabel } : {}),
        categories: folder.folders?.map(mapFolderToCategory),
    };
}

/**
 * Maps editorial content folders to the SCAPI Category shape used by navigation menus (L1 → L2 → L3).
 */
export function mapContentFoldersToCategories(contentFolders: ContentFolder[]): CatalogMenuCategory[] {
    return contentFolders.map(mapFolderToCategory);
}

export function getFolderSelfLink(item: CatalogMenuCategory): FolderSelfLink | null {
    if (!item.c_showSelfInMenu) {
        return null;
    }

    const href = createCategoryUrl(item);
    if (!href || href === '#') {
        return null;
    }

    const label = item.c_selfMenuLabel?.trim() || item.name || '';
    if (!label) {
        return null;
    }

    return {
        href,
        label,
        inEvidence: isCategoryInEvidence(item),
    };
}
